import React, { Dispatch, SetStateAction } from 'react';
import { useLocation } from 'react-router-dom';
import FrequencyButtonGroup from 'components/summary-tab/freq-button-group';
import Search from 'components/search';
import ExportBtn from 'components/modals/export/exportBtn';
import NoDataAvailable from 'components/empty-state';
import { SUMMARY } from 'state/summary/type';
import usePayment from 'state/payment/hooks/usePayment';
import TipSummaryByDropdown from './components/TipSummaryByDropdown';
import TipSummaryByPaymentType from './components/TipSummaryByPaymentType';
import TipSummaryByEmployee from './components/TipSummaryByEmployee';
import useTipsSummary from './hooks/useTipsSummary';
import { SummaryType } from '../types';

interface TipSummaryProps {
  excludeHourly?: boolean;
  showIntervalButton?: boolean;
  toSetSummaryType: Dispatch<SetStateAction<string>>;
  summaryType: string;
  activeTab: string;
  intervalTypes: SUMMARY.IIntervalType[];
  toSetIntervalTypes: Dispatch<SetStateAction<SUMMARY.IIntervalType[]>>;
  onExport: () => void;
}

const TipSummary = ({
  excludeHourly = true,
  showIntervalButton = true,
  toSetSummaryType,
  summaryType,
  activeTab,
  intervalTypes,
  toSetIntervalTypes,
  onExport,
}: TipSummaryProps): JSX.Element => {
  const location = useLocation();
  const { payment } = usePayment();

  const {
    comparisonDate,
    activeFreq,
    setActiveFreq,
    products,
  } = useTipsSummary({
    activeTab,
    summaryType,
    intervalTypes,
    toSetIntervalTypes,
    excludeHourly,
  });

  const isValidPaymentData =
    Object.values(payment?.tipsSummaryPaymentType ?? {}).some(
      (intervalData) =>
        Array.isArray(intervalData) &&
        intervalData.some((data) => data.value.number_of_tips > 0)
    ) || false;
  const isValidEmployeeData =
    payment?.tipsSummaryEmployeeType.number_of_tips > 0 || false;

  const showFrequencyButtons =
    products?.length > 0 &&
    showIntervalButton &&
    summaryType === SummaryType.PAYMENT_TYPE;

  return (
    <div>
      <div className="flex justify-between">
        <div className="flex items-center gap-3">
          <div className="text-sm text-gray-blue-70">Segmented by</div>
          <TipSummaryByDropdown
            selected={summaryType}
            setSummartType={toSetSummaryType}
          />
          {summaryType === SummaryType.EMPLOYEE && (
            <Search
              queryParams={location.search}
              placeHolders="Search by Employee name, Employee ID"
            />
          )}
        </div>
        <div className="flex items-center ml-auto">
          {showFrequencyButtons && (
            <FrequencyButtonGroup
              enabledFreq={intervalTypes}
              activeFreq={activeFreq}
              setActiveFreq={setActiveFreq}
            />
          )}
          <ExportBtn onClick={onExport} id="tips-export-btn" />
        </div>
      </div>

      {summaryType === SummaryType.PAYMENT_TYPE ? (
        isValidPaymentData ? (
          <TipSummaryByPaymentType
            summaryIntervalData={products}
            summaryTotalData={payment.tipsSummaryPaymentType}
            activeFreq={activeFreq}
            comparisonDate={comparisonDate}
          />
        ) : (
          <NoDataAvailable />
        )
      ) : isValidEmployeeData ? (
        <TipSummaryByEmployee
          summaryData={payment.tipsSummaryEmployeeType}
          previousStartDate={comparisonDate[0]}
          previousEndDate={comparisonDate[1]}
        />
      ) : (
        <NoDataAvailable />
      )}
    </div>
  );
};

export default TipSummary;
